<template>
    <div>
        <div class="title d-flex justify-content-between align-items-center mb-3">
            <div class="text mb-0">Export Staff</div>
            <div>
                <button
                    class="btn btn-dark"
                    @click="onExport"
                    :disabled="!selectedUsers.length || isLoading"
                >
                    <span
                        >Export
                        {{
                            selectedUsers.length > 0
                                ? "(" + selectedUsers.length + ")"
                                : ""
                        }}</span
                    >
                </button>
            </div>
        </div>

        <div
            class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-end mb-4"
        >
            <div class="mb-2 mb-md-0">
                <span v-if="selectedUsers.length" class="text-muted">
                    {{ selectedUsers.length }} staff{{ selectedUsers.length > 1 ? 's' : '' }} selected
                </span>
            </div>
            <div class="col-12 col-md-2 px-0">
                <input
                    type="search"
                    class="form-control"
                    v-model="searchQuery"
                    placeholder="Search staff"
                />
            </div>
        </div>

        <div class="table-responsive">
            <table class="table list">
                <thead>
                    <tr>
                        <th class="select-column">
                            <input
                                type="checkbox"
                                class="cursor-pointer"
                                :checked="isAllSelected"
                                @change="toggleSelectAll"
                            />
                        </th>
                        <th class="d-none d-md-table-cell">
                            User ID
                        </th>
                        <th>
                            <sorting
                                :order-by="filter.sortName"
                                @sorting="
                                    orderBy => sortingData(orderBy, 'name')
                                "
                                text="Name"
                            />
                        </th>
                        <th class="d-none d-md-table-cell">
                            <sorting
                                :order-by="filter.sortRole"
                                @sorting="
                                    orderBy => sortingData(orderBy, 'role')
                                "
                                text="Role"
                            />
                        </th>
                        <th class="d-none d-md-table-cell">
                            <sorting
                                :order-by="filter.sortEmail"
                                @sorting="
                                    orderBy => sortingData(orderBy, 'email')
                                "
                                text="Email"
                            />
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="isLoading">
                        <td colspan="6" class="text-center">
                            <loader-list />
                        </td>
                    </tr>
                    <tr v-else-if="!users.length">
                        <td colspan="6" class="text-center">
                            No users found
                        </td>
                    </tr>
                    <template v-else>
                        <tr
                            v-for="user in sortedUsers"
                            :key="user.id"
                            :class="{
                                'table-active': selectedUsers.includes(user.id)
                            }"
                            @click="onRowClick($event, user)"
                        >
                            <td class="select-column" @click.stop>
                                <input
                                    type="checkbox"
                                    class="cursor-pointer"
                                    :value="user.id"
                                    :checked="isUserSelected(user.id)"
                                    v-model="selectedUsers"
                                />
                            </td>
                            <td class="d-none d-md-table-cell">{{ maskUserId(user.id) }}</td>
                            <td>
                                <actor-name
                                    :actor="user"
                                    :use-encoded="true"
                                    :use-router="false"
                                    :new-tab="true"
                                    @click.native.stop
                                />
                                <div class="d-md-none small text-muted">
                                    {{ user.role }} • {{ user.email }}
                                </div>
                            </td>
                            <td class="d-none d-md-table-cell">{{ user.role }}</td>
                            <td class="d-none d-md-table-cell">{{ user.email }}</td>
                        </tr>
                    </template>
                </tbody>
            </table>
            <scroll-to-top :is-smooth="true" />
        </div>

        <cubex-side-panel
            v-model="showSidePanel"
            :items="allUsers"
            :title="`Selected Staff`"
            @confirm="confirmExport"
            @update:selected="updateSelectedUsers"
        />
    </div>
</template>

<script>
import CubexSidePanel from "@/components/setting-cubex/cubex-side-panel";
import LoaderList from "@/components/elements/loader-list";
import Sorting from "@/module/commons/sorting";
import CubexService from "@/services/setting/cubex";
import ActorName from "@/components/commons/actor-name";
import ScrollToTop from "@/components/commons/scroll-to-top";
import { debounce } from "lodash";

/**
 * ExportUsers Component
 * A component that manages the display and export functionality of user data in a table format.
 * Features include:
 * - Searchable user list
 * - Sortable columns (ID, Name, Role, Email)
 * - Bulk selection of users
 * - Export functionality with side panel confirmation
 */
export default {
    name: "ExportUsers",
    components: {
        LoaderList,
        Sorting,
        ActorName,
        CubexSidePanel,
        ScrollToTop
    },
    data() {
        return {
            isLoading: false,
            searchQuery: "",
            users: [],
            allUsers: [],
            showSidePanel: false,
            filter: {
                sortId: null,
                sortName: null,
                sortRole: null,
                sortEmail: null,
                search: null
            }
        };
    },
    computed: {
        selectedUsers: {
            get() {
                return this.$store.getters["cubex/selectedUsers"];
            },
            set(value) {
                this.$store.dispatch("cubex/setSelectedUsers", value);
            }
        },
        isAllSelected() {
            return (
                this.users.length > 0 &&
                this.selectedUsers.length === this.users.length
            );
        },
        sortedUsers() {
            let sorted = [...this.users];

            if (this.filter.sortId) {
                sorted.sort((a, b) => {
                    const aId = String(a.id);
                    const bId = String(b.id);

                    return this.filter.sortId === "asc"
                        ? aId.localeCompare(bId)
                        : bId.localeCompare(aId);
                });
            }
            if (this.filter.sortName) {
                sorted.sort((a, b) => {
                    return this.filter.sortName === "asc"
                        ? a.name.localeCompare(b.name)
                        : b.name.localeCompare(a.name);
                });
            }
            if (this.filter.sortRole) {
                sorted.sort((a, b) => {
                    return this.filter.sortRole === "asc"
                        ? a.role.localeCompare(b.role)
                        : b.role.localeCompare(a.role);
                });
            }
            if (this.filter.sortEmail) {
                sorted.sort((a, b) => {
                    return this.filter.sortEmail === "asc"
                        ? a.email.localeCompare(b.email)
                        : b.email.localeCompare(a.email);
                });
            }

            return sorted;
        }
    },
    watch: {
        searchQuery: {
            handler: debounce(function (newValue) {
                this.filter.search = newValue;
                this.fetchUsers();
            }, 300),
            immediate: false
        }
    },
    methods: {
        /**
         * Handles the sorting of table data
         * @param {string} orderBy - Sort direction ('asc' or 'desc')
         * @param {string} col - Column to sort by ('id', 'name', 'role', or 'email')
         */
        sortingData(orderBy, col) {
            // Reset all other sorts
            Object.keys(this.filter).forEach(key => {
                if (key.startsWith("sort")) {
                    this.filter[key] = null;
                }
            });

            // Set the new sort
            this.filter[
                `sort${col.charAt(0).toUpperCase() + col.slice(1)}`
            ] = orderBy;
        },

        /**
         * Toggles selection of all users in the table
         */
        toggleSelectAll() {
            if (this.isAllSelected) {
                this.selectedUsers = [];
            } else {
                this.selectedUsers = this.users.map(user => user.id);
            }
        },

        /**
         * Initiates the export process by showing the side panel
         */
        onExport() {
            if (!this.selectedUsers.length) return;
            this.showSidePanel = true;
        },

        /**
         * Processes the export of selected users
         * @param {number[]} selectedUserIds - Array of selected user IDs to export
         */
        async confirmExport(selectedUserIds) {
            if (!selectedUserIds.length) return;

            this.isLoading = true;
            const param =
                selectedUserIds.length == this.users.length
                    ? []
                    : selectedUserIds;

            try {
                const response = await CubexService.exportUsers(param);

                if (response?.code === 200 && response?.path) {
                    // download file from given url
                    window.open(response?.path, "_blank");

                    this._message("Staff exported successfully");
                    this.selectedUsers = [];
                    this.fetchUsers();
                } else {
                    this._message(
                        response?.message || "Failed to export users",
                        "danger"
                    );
                }
            } catch (error) {
                console.error("Export error:", error);
                this._message("Failed to export users", "danger");
            } finally {
                this.isLoading = false;
            }
        },

        /**
         * Fetches users from the API based on search criteria
         */
        async fetchUsers() {
            this.isLoading = true;

            try {
                const response = await CubexService.getUsers({
                    businessId: this._setting.business.id,
                    search: this.filter.search
                });

                if (response?.code === 200 && response?.resources) {
                    const mappedUsers = response.resources.map(user => ({
                        id: user.id,
                        name: `${user.firstname} ${user.lastname}`,
                        email: user.email,
                        role: user.role,
                        ...user
                    }));

                    if (!this.filter.search) {
                        // Only update allUsers when there's no search filter
                        this.allUsers = mappedUsers;
                    }

                    this.users = mappedUsers;
                } else {
                    this._message("Failed to load users", "danger");
                }
            } catch (error) {
                console.error("Fetch error:", error);
                this._message("Failed to load users", "danger");
            } finally {
                this.isLoading = false;
            }
        },

        /**
         * Masks the user ID for display
         * @param {number} id - The user ID to mask
         * @returns {string} The masked user ID
         */
        maskUserId(id) {
            const idString = String(id);
            const firstElevenDigits = idString.slice(11);
            const maskedPart = "*".repeat(11);
            return `${maskedPart}${firstElevenDigits}`;
        },

        updateSelectedUsers(newSelected) {
            this.selectedUsers = [...newSelected];
        },

        isUserSelected(userId) {
            return this.selectedUsers.includes(userId);
        },

        onRowClick(event, user) {
            // Don't trigger if clicking on a link or other interactive element
            if (event.target.tagName.toLowerCase() === 'a' || 
                event.target.parentElement.tagName.toLowerCase() === 'a') {
                return;
            }
            
            // Toggle the selection
            if (this.isUserSelected(user.id)) {
                const index = this.selectedUsers.indexOf(user.id);
                if (index > -1) this.selectedUsers.splice(index, 1);
            } else {
                this.selectedUsers.push(user.id);
            }
        }
    },
    mounted() {
        this.fetchUsers();
    }
};
</script>

<style scoped>
.table th {
    background-color: #f8f9fa;
    white-space: nowrap;
    padding: 0.75rem;
}

.table td,
.table th {
    vertical-align: middle;
}

.table-active {
    background-color: rgba(0, 0, 0, 0.025);
}

.table tr:hover {
    background-color: rgba(0, 0, 0, 0.025);
}

.select-column {
    width: 40px;
}

@media (max-width: 767.98px) {
    .title {
        flex-direction: column;
        align-items: flex-start;
        gap: 1rem;
    }
    
    .title .btn {
        width: 100%;
    }
    
    .table td {
        padding: 0.5rem;
    }
}
</style>
